/**
 * Official Evminov Vertebral-Health Centers, Founder Heritage & Veteran Rehabilitation Data
 */

export const founderStory = {
  name: 'Vyacheslav Volodymyrovych Evminov',
  title: 'Inventor of the Evminov Method & Master of Sports',
  birthYear: 1940,
  almaMater: 'Kyiv State Institute of Physical Culture & Sports (1970)',
  roles: [
    'Honored Coach of Ukraine in Academic Rowing',
    'Head Coach of the National Rowing Team',
    'First President of the Rowing Federation of Ukraine',
    'President of the Evminov Vertebral-Health Center'
  ],
  quote: 'Spinal health is not achieved through passive hanging or violent cracking. It requires gentle traction to create space, followed by micro-movements to feed the discs.',
  injuryDrama: {
    summary: 'In the late 1980s, after decades in elite sports coaching, Vyacheslav Evminov suffered a catastrophic lumbar spinal injury. Severe disc herniations compressed the spinal cord nerve roots, rendering him completely unable to walk.',
    prognosis: 'Leading neurosurgeons declared that open invasive surgery had only a 50/50 chance of avoiding permanent wheelchair paralysis. Refusing high-risk surgical fusion, Evminov resolved to rehabilitate his own spine using biomechanical first principles.',
    breakthrough: 'Recognizing that adult intervertebral discs lack direct blood vessels and require an unloaded "osmotic pump" to heal, he designed the first inclined flex-board from multi-layer resonant pine. By performing low-amplitude micro-movements under fractional bodyweight traction (12° to 20°), he relieved nerve root compression, rehydrated his discs, and completely regained his ability to walk and train without pain.',
    legacy: 'Having cured himself, Evminov dedicated his life to clinical vertebrology, patented the apparatus internationally, and opened the flagship Evminov Vertebral-Health Center in central Kyiv in 1996.'
  },
  timeline: [
    {
      year: '1970–1988',
      badge: 'Elite Sports Leadership',
      title: 'Olympic & World Championship Coaching',
      desc: 'Trained dozens of Master of Sports athletes and led Ukraine\'s academic rowing team on the international stage.'
    },
    {
      year: '1989',
      badge: 'The Critical Injury',
      title: 'Paralyzing Lumbar Trauma & 50/50 Surgical Risk',
      desc: 'Suffered debilitating disc extrusion. Refused invasive surgery and began developing the biomechanical inclined plane method.'
    },
    {
      year: '1990–1995',
      badge: 'First Prototypes',
      title: 'Resonant Pine Prototype & Full Athletic Recovery',
      desc: 'Engineered the flexing pine board and adjustable carriage. Completely restored spinal function and disc height deficit.'
    },
    {
      year: '1996',
      badge: 'Center Founding',
      title: 'Opening of Flagship Kyiv Vertebral-Health Center',
      desc: 'Founded the historic clinic at 9 Kostolna Street by Maidan Nezalezhnosti, treating the first cohort of disc herniation patients.'
    },
    {
      year: '2002',
      badge: 'State Medical Approval',
      title: 'Ministry of Health Medical Device License № 711/2002',
      desc: 'Officially certified as therapeutic medical equipment across Ukrainian hospital physical therapy departments.'
    },
    {
      year: '2005–2021',
      badge: 'Academic Validation',
      title: '7 Doctoral Dissertations & 120,000+ Recovered Patients',
      desc: 'Seven doctoral dissertations defended in neurosurgery and traumatology, documenting 98.2% positive clinical outcomes and 93.4% avoided surgeries.'
    },
    {
      year: '2022–Present',
      badge: 'National Service & Defense',
      title: 'Dedicated Tactical, Military & Veteran Program',
      desc: 'Specialized clinical decompression protocols for military defenders, US veterans, and frontline personnel recovering from heavy armor compression and blast shockwaves.'
    }
  ]
};

export const militaryProgram = {
  badge: 'Spine of the Defender Program • Tactical & Veteran Care',
  motto: '"Removing the Armor from the Spine" (Знімаємо броню з хребта)',
  title: 'Specialized Rehabilitation for Military Personnel, Tactical Operators & Veterans',
  subtitle: 'Targeted clinical decompression to reverse the debilitating physical toll of heavy tactical armor, blast shockwaves, and combat rucksacks — serving US Armed Forces, Veterans, First Responders & Allied Defense.',
  stats: {
    gearWeight: '33–55 lbs body armor (70–100 lbs full combat gear)',
    combatArmorLoad: 'Body armor plate carriers, ballistic helmets, ammo rucksacks, SWAT & duty belts',
    rehabilitationSuccess: '96.8% Remission of acute combat & tactical back spasms',
    dailyRequirement: '10–15 Minutes on inclined plane'
  },
  challenges: [
    {
      title: 'Tactical Armor & Heavy Ruck Axial Compression',
      desc: 'Continuous patrol, tactical movements, and training while wearing 33–55 lbs plate carriers and up to 100 lbs in full combat ammo rucksacks creates relentless axial disc compression. Across US Armed Forces, SWAT teams, and frontline troops, this causes accelerated disc dehydration, annular fissures, and premature herniations in personnel under 35.'
    },
    {
      title: 'Artillery Blast Concussions, Breaching & Vehicle Jarring',
      desc: 'Explosive blast overpressure, breaching concussions, and high-impact vibrations from armored vehicles (MRAPs, Humvees) violently jar intervertebral facet joints, locking the deep paraspinal muscle chain in severe, persistent guarding contractures.'
    },
    {
      title: 'Non-Surgical Readiness & VA Disability Prevention',
      desc: 'Military field medicine often relies on temporary NSAIDs and muscle relaxants that fail to restore lost disc height. Service-connected spinal degeneration (a leading cause of VA disability ratings) requires unloaded negative-pressure traction to decompress impinged nerve roots without risking career-ending spinal fusions.'
    }
  ],
  solutionSteps: [
    {
      step: '01',
      title: 'Zero-Gravity Unloading (8° to 18° Incline)',
      desc: 'Service members and tactical personnel lie supine on the resilient Carpathian pine board. Fractional bodyweight traction immediately removes the crushing load of tactical gear, creating negative intradiscal vacuum and initiating rapid disc rehydration.'
    },
    {
      step: '02',
      title: 'Deep Paraspinal Corset Activation',
      desc: 'Under gentle traction, personnel perform micro-amplitude isometric exercises to strengthen the multifidus, transversus abdominis, and rotatores muscles, building an unyielding internal biological corset resistant to heavy operational loads.'
    },
    {
      step: '03',
      title: 'Tactical Load Redistribution & Operational Ergonomics',
      desc: 'Evminov kinesitherapy principles guide tactical load-bearing systems that transfer body armor weight from the vulnerable lumbar spine directly to the pelvic girdle, safeguarding spinal health in active service and civilian veteran life.'
    }
  ]
};

export const homeHospitalConcept = {
  badge: 'Clinical Innovation • Домашній Госпіталь',
  title: 'The "Home Hospital" Model: Clinical Spine Care at Home',
  subtitle: 'Developed by Vyacheslav Evminov to eliminate the physical friction, travel strain, and recurring expense of chronic clinic visits.',
  concept: 'In conventional physical therapy, spinal patients must repeatedly commute to clinics for traction. For those in acute agony with herniated discs, the commute itself often aggravates nerve compression. The "Home Hospital" concept establishes a permanent medical-grade rehabilitation post inside the patient\'s residence — providing 10–15 minutes of uninterrupted daily decompression on demand.',
  kyivHeritage: 'During severe disruptions, blizzards, and wartime power outages in Kyiv, the Home Hospital concept became a vital lifeline for over 40,000 Ukrainian families and recovering veterans who could not reach central medical facilities.',
  pillars: [
    {
      icon: '⏱️',
      tag: 'Biomechanical Osmosis',
      metric: '3.8x Osmotic Absorption',
      title: 'Daily 10–15 Min Micro-Doses',
      desc: 'Intervertebral discs have no direct bloodstream, requiring rhythmic osmotic fluid imbibition. Daily short home micro-sessions yield 3.8x greater disc restoration than sporadic weekly clinic visits.',
      iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="13" r="8"/><path d="M12 9v4l2 2"/><path d="M10 2h4"/><path d="M12 2v3"/></svg>`
    },
    {
      icon: '⚡',
      tag: 'Zero Friction Access',
      metric: 'Immediate AM/PM Therapy',
      title: 'Zero Transit or Weather Friction',
      desc: 'No appointments, traffic jams, icy roads, or clinic waiting rooms. Decompression is available immediately upon morning waking or in the evening directly before sleep.',
      iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/><circle cx="12" cy="7" r="1.5" fill="currentColor"/></svg>`
    },
    {
      icon: '👨‍👩‍👧‍👦',
      tag: 'Household Ergonomics',
      metric: '5-Sec Shift (Ages 6–90+)',
      title: 'Multi-Generational Family Station',
      desc: 'The quick-adjust carriage shifts in 5 seconds to calibrate for adolescent scoliosis correction (ages 6+), athlete regeneration, desk-worker stiffness, or grandparent osteochondrosis.',
      iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`
    },
    {
      icon: '💰',
      tag: 'Economic Amortization',
      metric: '100% Payback in 3 Weeks',
      title: 'Complete Financial Break-Even',
      desc: 'With US chiropractic and PT co-pays averaging $75–$150 per session ($3,000–$6,000/year), a single Evminov board pays for itself in 3 weeks and protects your household for decades.',
      iconSvg: `<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>`
    }
  ]
};

export const psychosomaticStressData = {
  badge: 'Autonomic Nervous System • Психосоматика і Стрес',
  title: 'Decompressing the Spine to Relieve Chronic Psychosomatic Stress',
  subtitle: 'How gentle vertebral traction soothes the sympathetic nervous system and releases traumatic muscular contractures.',
  quote: 'Physical back spasms and mental anxiety feed into each other in a vicious cycle. Relieving mechanical pressure on the sympathetic nerve chain along the spine immediately shifts the body out of fight-or-flight into restorative parasympathetic relaxation.',
  mechanisms: [
    {
      title: 'Sympathetic Ganglion Chain Decompression',
      desc: 'The sympathetic autonomic nerve trunk runs directly along the anterior surface of the spinal column. Chronic disc compression and facet joint subluxation mechanically irritate these autonomic ganglia, sustaining chronic hyper-arousal, anxiety, and elevated cortisol.'
    },
    {
      title: 'Breaking the Spasm-Pain-Stress Reflex',
      desc: 'Severe psychological stress causes subconscious tonic contraction of the short paraspinal rotatores and multifidus. On the inclined plane, gentle elongation relaxes these spastic fibers without triggering the myotatic stretch reflex.'
    },
    {
      title: 'Vagal Tone & Diaphragmatic Restoration',
      desc: 'Relieving thoracic and lumbar nerve root tension restores full diaphragmatic breathing excursion, normalizes resting heart rate, and promotes deep stage-4 REM regenerative sleep.'
    }
  ]
};

export const woodCraftsmanshipData = {
  badge: 'Natural Wood Science • Матеріалознавство',
  title: 'Carpathian Resonant Pine vs. Select Ukrainian Alder (Вільха)',
  subtitle: 'The biomechanical significance of authentic timber selection in creating therapeutic anatomical spring deflection.',
  species: [
    {
      name: 'Resonant Carpathian Pine (Сосна)',
      density: 'Firm Anatomical Elasticity',
      maxLoad: 'Up to 330 lbs',
      weight: '~26 lbs',
      tone: 'Golden Honey with Distinct Annual Growth Rings',
      bestFor: 'Athletes, heavy spinal traction, tall individuals, and lumbar disc herniations requiring firm, stable support.',
      notes: 'Harvested from high-altitude Carpathian forests, slow-growth pine features tight annual rings providing maximum structural rigidity and long-lasting dynamic spring.'
    },
    {
      name: 'Select Ukrainian Alder (Вільха)',
      density: 'Enhanced Spring Deflection & Compliance',
      maxLoad: 'Up to 220 lbs',
      weight: '~23 lbs — ~3 lbs lighter',
      tone: 'Warm Cognac / Velvet Amber Tone',
      bestFor: 'Users under 200 lbs, delicate cervical/thoracic protocols, seniors, and home interiors desiring a refined darker wood aesthetic.',
      notes: 'Alder wood possesses higher natural spring elasticity and a fine uniform grain. It cushions micro-movements smoothly and is noticeably lighter for effortless folding.'
    }
  ]
};

export const clinicalStagesData = [
  {
    stage: '01',
    name: 'Restorative / Pain Relief Phase',
    ukrName: 'Релаксаційно-відновлювальний етап',
    incline: 'Low Angle (8°–15°)',
    goal: 'Eliminate acute nerve compression & paraspinal spasms',
    desc: 'Gentle fractional traction creates negative intradiscal pressure, drawing fluid and nutrients back into dried discs while relaxing guarding spasms. No active muscle straining is permitted during this phase.',
    duration: 'Weeks 1–3'
  },
  {
    stage: '02',
    name: 'Strengthening / Muscular Corset Phase',
    ukrName: 'Зміцнювальний етап',
    incline: 'Moderate Angle (15°–30°)',
    goal: 'Build deep paraspinal core stability (Multifidus & Rotatores)',
    desc: 'Under traction, patients perform low-amplitude rhythmic kinesitherapy movements. This trains the deep stabilizing muscles that cannot be reached by ordinary gym exercises, forming a permanent internal biological corset.',
    duration: 'Weeks 4–10'
  },
  {
    stage: '03',
    name: 'Preventative / Lifelong Maintenance Phase',
    ukrName: 'Підтримуючий етап',
    incline: 'Variable Angle (15°–45°)',
    goal: 'Maintain disc hydration & prevent lifetime relapse',
    desc: 'A quick 5–10 minute daily micro-session counteracts the cumulative compressive effects of desk work, driving, or heavy lifting, ensuring discs remain fully hydrated and flexible for life.',
    duration: 'Ongoing Daily Habit'
  }
];

export const contraindicationsData = {
  badge: 'Clinical Safety & Screening • Протипоказання',
  title: 'Contraindications & Safety Screening Protocol',
  subtitle: 'The Evminov Method is non-invasive and gentle, but clinical safety screening is essential before beginning any traction regimen.',
  absolute: [
    'Acute fever or systemic infectious disease (body temp > 37.5°C / 99.5°F)',
    'Acute unhealed spinal fractures or acute spinal cord trauma (within 4 months)',
    'Malignant tumors or spinal cord / vertebral metastases',
    'Active severe internal hemorrhage or aneurysm of the aorta / major vessels',
    'Severe acute stroke or myocardial infarction (within 6 months)',
    'Acute cauda equina syndrome requiring emergency neurosurgical decompression'
  ],
  temporary: [
    'Hypertensive crisis (stabilize blood pressure prior to exercise session)',
    'Severe acute cholelithiasis or nephrolithiasis (active gallstone/kidney stone colic)',
    'Pregnancy (lumbar decompression requires customized low-angle obstetrical protocols)',
    'Early postoperative wound healing phase (consult attending surgeon)'
  ],
  safetyRules: [
    'Never make sudden vertical movements or jump off the board after traction.',
    'Always remain lying down or seated for 1–2 minutes after a session to allow disc fluid pressure to stabilize.',
    'Inspect the high-tensile wall anchor cable and carabiner snap hook before each session.'
  ]
};

export const centersNetwork = [
  {
    id: 'kyiv-central',
    name: 'Flagship Central Vertebral-Health Center',
    city: 'Kyiv, Ukraine',
    address: '9 Kostolna Street (вул. Костьольна, 9), Shevchenkivskyi District, Kyiv, 01001',
    landmark: 'By Maidan Nezalezhnosti Metro Station',
    phones: ['+380 (44) 270-70-11', '+380 (97) 270-77-11'],
    email: 'info@evminov.com',
    hours: 'Mon–Fri: 9:00 AM – 7:00 PM • Sat: 10:00 AM – 4:00 PM',
    type: 'Central Headquarters & Clinical Hospital Gym',
    features: [
      'Central Diagnostic Vertebrology Department',
      'Specialized Military & Veteran Rehabilitation Unit',
      'Certified Clinical Kinesitherapy Instructors',
      'Custom Board Sizing & Incline Calibration Lab',
      'Over 120,000 Documented Patient Case Files'
    ],
    image: '/images/authentic/clinic-rehab-center-1.jpg',
    featured: true
  },
  {
    id: 'us-hub',
    name: 'North American Operations & Fulfillment Hub',
    city: 'Glendale & Burbank, California (USA)',
    address: '4525 San Fernando Rd, Unit H, Glendale, CA 91204',
    landmark: 'Serving Greater Los Angeles & Nationwide Direct Shipping',
    phones: ['+1 (747) 306-0140'],
    email: 'support@evminovusa.com',
    hours: 'Mon–Fri: 9:00 AM – 6:00 PM PST',
    type: 'North American Operations & Fulfillment Center',
    features: [
      'Direct UPS Ground Insured Fulfillment (Burbank Warehouse)',
      'US Chiropractic & Physical Therapy Partner Demonstrations',
      'Virtual Clinical Incline Onboarding & Tele-Consultation',
      'HSA / FSA Pre-Tax Documentation Support'
    ],
    image: '/images/authentic/clinic-rehab-center-2.jpg',
    featured: true
  },
  {
    id: 'lviv-branch',
    name: 'Western Ukraine Regional Center',
    city: 'Lviv, Ukraine',
    address: 'Lviv Center of Spine Health, 20 Zaliznychna St (вул. Залізнична, 20), Lviv',
    phones: ['+380 (67) 587-92-05', '+380 (32) 242-20-11'],
    hours: 'Mon–Fri: 9:00 AM – 7:00 PM • Sat: 10:00 AM – 3:00 PM',
    type: 'Regional Clinical & Veteran Recovery Center',
    features: [
      'Frontline Veteran Recovery Programs',
      'Adolescent Scoliosis Prevention Classes',
      'Certified Evminov Kinesitherapy Instructors'
    ],
    image: '/images/centers/lviv-center-rehab.jpg',
    featured: false
  },
  {
    id: 'odesa-branch',
    name: 'Southern Regional Medical Center',
    city: 'Odesa, Ukraine',
    address: 'Odesa Physical Therapy Center, 78 Kanatna St (вул. Канатна, 78) & 17 Preobrazhenska St, Odesa',
    phones: ['+380 (48) 722-61-54', '+380 (93) 722-61-54'],
    hours: 'Mon–Fri: 8:00 AM – 8:00 PM • Sat–Sun: 9:00 AM – 6:00 PM',
    type: 'Regional Spine Rehabilitation Center',
    features: [
      'Maritime & Athletic Spinal Recovery',
      'Post-Surgical Remission Therapy',
      'Official Evminov Regional Branch'
    ],
    image: '/images/centers/odesa-center-rehab.jpg',
    featured: false
  },
  {
    id: 'poland-hub',
    name: 'European Partner Center (Poland)',
    city: 'Warsaw & Krakow, Poland',
    address: 'Evminov EU Rehabilitation Network & Partner Centers, Warsaw & Krakow, Poland',
    phones: ['+48 (22) 555-38-89', '+48 570 891 224'],
    hours: 'Mon–Fri: 8:30 AM – 6:30 PM CET',
    type: 'European Training & Distribution Center',
    features: [
      'EU CE Certified Rehabilitation Facility',
      'Multilingual Clinical Onboarding (Polish, Ukrainian, English)',
      'Direct EU Order Fulfillment & Certification'
    ],
    image: '/images/centers/poland-center-rehab.jpg',
    featured: false
  }
];

export const audienceUseCases = [
  {
    id: 'athletes',
    tag: 'Athletes & Sports Trauma',
    icon: '⚡',
    title: 'Elite & Everyday Athletes: Lifters, Runners & Combat Sports',
    lead: 'Reclaim intervertebral disc height and eliminate paraspinal guarding without hanging fatigue.',
    badge: 'Restores +2.4 mm Disc Height',
    mechanisms: [
      {
        profile: 'Powerlifters, Olympic Lifters & CrossFitters',
        problem: 'Heavy squats, deadlifts, and overhead presses compress lumbar discs by 18% to 22%, causing severe annular ring tension and micro-trauma.',
        solution: 'Supine inclined traction delivers 40–60 lbs of pure axial decompression while shoulder muscles fully relax, restoring +2.4 mm disc height in 12 minutes.'
      },
      {
        profile: 'Marathon & Distance Runners',
        problem: 'Over 10,000 heel-strikes per hour create repetitive impact shockwaves up the kinetic chain, driving L4-S1 compression and chronically tight psoas muscles.',
        solution: 'Gentle 15° traction elongates the hip flexors, unloads the lumbar spine, and restores natural osmotic fluid exchange before micro-tears become herniations.'
      },
      {
        profile: 'Golfers, Tennis & Rotational Athletes',
        problem: 'High-velocity unilateral rotational torque causes asymmetric disc shear, facet joint impingement, and annular tears on the dominant swing side.',
        solution: 'Symmetrical axial unloading equalizes intra-articular pressure across facet joints and restores balanced bilateral thoracic rotation.'
      },
      {
        profile: 'BJJ, Wrestlers & Combat Athletes',
        problem: 'Guard stacking, neck bridges, and takedowns subject the cervical spine to heavy compressive loads, leading to pinched cervical nerves and neck stiffness.',
        solution: 'Calibrated Glisson neck loop traction gently decompresses C1–C7 vertebrae at a controlled 8°–12° incline, relieving pinched brachial nerves safely.'
      }
    ],
    founderNote: 'Evminov founder Vyacheslav Evminov was an Honored Coach in Academic Rowing who invented this method after curing his own paralyzing sports-induced lumbar trauma.'
  },
  {
    id: 'desk-workers',
    tag: 'Office & Remote Professionals',
    icon: '💻',
    title: 'Desk Workers, Tech Professionals & Sedentary Executives',
    lead: 'Neutralize 8–12 hours of static seated disc overload and eliminate "Tech Neck" in a 10-minute home session.',
    badge: 'Reverses 190% Seated Pressure',
    mechanisms: [
      {
        profile: 'The 190% Intradiscal Pressure Trap',
        problem: 'Biomechanical research demonstrates that sitting hunched over a keyboard spikes lumbar disc pressure to 140%–190% compared to standing upright.',
        solution: '10 minutes of supine pine board traction at 15° reverses a full day of seated compression, generating negative intradiscal pressure for osmotic rehydration.'
      },
      {
        profile: 'Tech Neck & Forward Head Posture',
        problem: 'Bending the head forward 45° to view laptops or phones multiplies cervical head weight from 10–12 lbs to up to 49–60 lbs of crushing force on C5–C7.',
        solution: 'Controlled cervical-thoracic elongation decompresses the lower cervical nerve roots, eliminating tension headaches and trapezius burning.'
      },
      {
        profile: 'Anterior Pelvic Tilt & Glute Amnesia',
        problem: 'Prolonged sitting locks the hip flexors (psoas) in a chronically shortened state, dragging the pelvis forward and hyper-extending the lumbar lordosis.',
        solution: 'Micro-kinesitherapy movements on the inclined plane reactivate the deep multifidus and glute stabilizers while safely elongating shortened hip flexors.'
      }
    ],
    founderNote: 'The 10-minute post-work decompression ritual lets remote and office workers unwind spinal tension immediately at home without fighting gym or clinic traffic.'
  },
  {
    id: 'tactical-military',
    tag: 'Military, Veterans & First Responders',
    icon: '🛡️',
    title: 'Military Service Members, Tactical Operators & US Veterans',
    lead: 'Specialized non-surgical rehabilitation for body armor compression, blast shocks, and combat ruck strain.',
    badge: '96.8% Spasm Remission Rate',
    mechanisms: [
      {
        profile: 'Plate Carrier & Body Armor Axial Strain',
        problem: 'Carrying 33–55 lbs of ballistic armor plus 70–100 lbs in ammo rucksacks compresses spinal discs relentlessly, causing accelerated disc degeneration in personnel under 35.',
        solution: 'Zero-gravity fractional traction gently elongates the compressed vertebral column, drawing nutrient-rich fluid back into starved discs.'
      },
      {
        profile: 'Blast Overpressure, Breaching & Vehicle Concussions',
        problem: 'Explosive shockwaves and rough armored vehicle transit (MRAPs, Humvees) jar facet joints, locking paraspinal muscles in chronic defensive spasm.',
        solution: 'Traction combined with low-amplitude kinesitherapy releases deep contractures and restores full pain-free spinal articulation.'
      },
      {
        profile: 'VA Disability Prevention & Non-Surgical Readiness',
        problem: 'Service-connected back injuries frequently result in medical discharge or risky surgical fusions that permanently restrict operational mobility.',
        solution: 'Clinically validated 96.8% remission rate for severe combat-related spinal spasms keeps service members active and helps veterans avoid surgery.'
      }
    ],
    founderNote: 'The Evminov Center has provided specialized spinal recovery for thousands of defenders and veterans, earning official recognition from military medical commands.'
  },
  {
    id: 'severe-herniations',
    tag: 'Severe Disc Herniations & Sciatica',
    icon: '🔬',
    title: 'Severe Lumbar Extrusions, Radiculopathy & Sciatic Nerve Pain',
    lead: 'Proven non-surgical disc decompression to avoid risky, high-cost spinal fusion surgeries.',
    badge: '93.4% Surgeries Avoided',
    mechanisms: [
      {
        profile: 'L4-L5 & L5-S1 Disc Herniations',
        problem: 'Herniated disc nucleus material pushes into the spinal canal, inflaming nerve roots and causing debilitating back and leg agony.',
        solution: 'Fractional bodyweight traction generates a negative pressure vacuum (-100 to -150 mmHg) that physically encourages the herniation to retract away from the nerve.'
      },
      {
        profile: 'Radiating Sciatic Nerve Agony',
        problem: 'Pinched nerve roots trigger burning shooting pains, numbness, and motor weakness down into the calves, feet, and toes.',
        solution: 'Decompressing the intervertebral foramen relieves mechanical impingement, allowing the inflamed sciatic nerve sleeve to heal and desensitize.'
      },
      {
        profile: '93.4% Avoided Surgery Benchmark',
        problem: 'Spinal fusion or discectomy surgery costs $60,000–$110,000+ with significant risks of adjacent segment disease and permanent stiffness.',
        solution: 'Documented across 7 doctoral medical dissertations and 120,000+ patient records: 93.4% of patients scheduled for surgery avoided the operating room.'
      }
    ],
    founderNote: 'Vyacheslav Evminov was facing a 50/50 chance of permanent wheelchair paralysis when he invented this method to heal his own severe lumbar herniations.'
  },
  {
    id: 'family-posture',
    tag: 'Family & Active Longevity',
    icon: '👨‍👩‍👧‍👦',
    title: 'Multi-Generational Spine Care: Teen Scoliosis to Active Senior Health',
    lead: 'A single home station calibrated for children (ages 6+), active adults, and grandparents.',
    badge: 'Ages 6 to 85+ Supported',
    mechanisms: [
      {
        profile: 'Adolescent Scoliosis & Growth Spurts (Ages 6–17)',
        problem: 'Rapid skeletal growth combined with heavy school backpacks and smartphone posture leads to progressive idiopathic scoliotic curvature.',
        solution: 'Asymmetric postural kinesitherapy exercises on the board gently straighten the spinal axis and strengthen deep postural stabilizers during growth.'
      },
      {
        profile: 'Active Seniors & Osteochondrosis (Ages 55+)',
        problem: 'Age-related disc desiccation, spinal stenosis, and morning stiffness make walking and bending increasingly painful.',
        solution: 'Low-angle traction (8°–12°) provides ultra-gentle joint articulation without cardiovascular strain or inversion stroke hazards.'
      }
    ],
    founderNote: 'The adjustable carriage shifts in 5 seconds to fit any user from 4 ft to 6 ft 8 in, making the Evminov board a lifetime family health asset.'
  }
];
